import { CheckCircle2, Circle } from "lucide-react";
import { Card } from "@/components/Common";
import { filled } from "@/components/profile/options";
import { cn } from "@/lib/utils";

const WEIGHTS = [
  ["target_roles", "Add target roles", 15, "goals"], ["skills", "List your hard skills", 12, "skills"],
  ["years_experience", "Set years of experience", 10, "snapshot"], ["career_level", "Pick your career level", 10, "snapshot"],
  ["work_models", "Choose a work model", 8, "snapshot"], ["preferred_states", "Pick target states", 8, "goals"],
  ["salary_base_min", "Set a salary floor", 7, "goals"], ["work_authorization", "Confirm work authorization", 7, "snapshot"],
  ["tech_stack", "Add your tech stack", 7, "skills"], ["bio", "Write an elevator pitch", 6, "snapshot"],
  ["current_title", "Add your current role", 5, "snapshot"], ["education_level", "Add your education", 5, "snapshot"],
  ["target_industries", "Add target industries", 5, "goals"], ["linkedin_url", "Link your LinkedIn", 5, "links"],
  ["certifications", "List certifications", 3, "snapshot"], ["soft_skills", "Add soft skills", 3, "skills"],
];
const TOTAL = WEIGHTS.reduce((s, w) => s + w[2], 0);

export const ProfileStrength = ({ f }) => {
  const missing = WEIGHTS.filter(([k]) => !filled(f[k]));
  const score = Math.round(((TOTAL - missing.reduce((s, w) => s + w[2], 0)) / TOTAL) * 100);
  const done = WEIGHTS.length - missing.length;
  const label = score >= 85 ? "Elite-ready" : score >= 60 ? "Strong" : score >= 35 ? "Getting there" : "Just started";
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  return (
    <Card className="border-[#D4AF37]/30" data-testid="profile-strength">
      <div className="flex items-end justify-between">
        <div><h3 className="font-display text-lg text-zinc-100">Match strength</h3><p className="text-xs text-zinc-500">{label} · {done}/{WEIGHTS.length} signals</p></div>
        <span className="font-display text-4xl font-semibold text-[#F3C649]" data-testid="profile-strength-score">{score}</span>
      </div>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-[#27272A]"><div className="h-full rounded-full bg-gradient-to-r from-[#9c7a1f] to-[#F3C649] transition-[width] duration-700" style={{ width: `${score}%` }} /></div>
      {missing.length > 0 ? (
        <ul className="mt-5 space-y-1" data-testid="profile-strength-tips">
          {missing.slice(0, 5).map(([k, l, w, sec]) => (
            <li key={k}>
              <button onClick={() => go(sec)} data-testid={`strength-tip-${k}`} className="group flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-[#D4AF37]/5">
                <Circle className="h-4 w-4 shrink-0 text-[#D4AF37]/60" />
                <span className="flex-1 text-sm text-zinc-300 group-hover:text-zinc-100">{l}</span>
                <span className="font-mono text-[11px] text-[#F3C649]">+{w}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : <p className="mt-4 text-sm text-[#F3C649]">Every match signal is filled. Your matches are as sharp as they get.</p>}
      {done > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-zinc-500">{done} completed</summary>
          <ul className="mt-2 space-y-1.5">{WEIGHTS.filter(([k]) => filled(f[k])).map(([k, l]) => <li key={k} className={cn("flex items-center gap-2 text-xs text-zinc-500")}><CheckCircle2 className="h-3.5 w-3.5 text-[#D4AF37]" />{l}</li>)}</ul>
        </details>
      )}
    </Card>
  );
};
